import { FormLabel } from "@mui/material";
import { memo, useState } from "react";
import { NumericFormat } from "react-number-format";
import styles from "../../TransactionsTableComponent/transactions-table-component/transactions-table-component.module.scss";
import { ButtonComponent } from "@zenith/components";

interface AmountTransactionFilterData {
  maximumValue: number | undefined;
  minimumValue: number | undefined;
  clear: () => void;
  apply: (min: number | undefined, max: number | undefined) => void;
}

const AmountTransactionFilter = memo(function AmountTransactionFilter(
  props: AmountTransactionFilterData
) {
  const [minValue, setMinValue] = useState<number | undefined>(
    props.minimumValue
  );
  const [maxValue, setMaxValue] = useState<number | undefined>(
    props.maximumValue
  );
  const [lastUpdate, setLastUpdated] = useState<"min" | "max">();

  function clearFilter() {
    props.clear();
    setMinValue(undefined);
    setMaxValue(undefined);
  }

  function isValuesValid(): boolean {
    if (minValue && maxValue) {
      return minValue <= maxValue;
    }
    return true;
  }

  const isValid = isValuesValid();

  return (
    <div className="w-filter-modal py-2">
      <div className="minimum-container mb-4 flex w-full flex-wrap px-2">
        <FormLabel className="pl-1.5 text-xs text-neutral-500">
          Minimum
        </FormLabel>
        <div
          className={
            "w-full focus:border-transparent focus:ring-0 " +
            styles.currencyWrapper
          }
        >
          <NumericFormat
            allowedDecimalSeparators={[","]}
            decimalSeparator="."
            allowNegative={false}
            placeholder="0.00"
            id="minimum-amount-filter"
            className={`h-10 w-full rounded-1.5lg border pl-38 pr-4 text-sm leading-normal text-neutral-900 outline-none focus:border-neutral-900 ${
              !isValid && lastUpdate === "min" && "border-red-400"
            }`}
            decimalScale={2}
            fixedDecimalScale
            value={minValue || ""}
            onValueChange={(value) => {
              setMinValue(value.floatValue);
              setLastUpdated("min");
            }}
            thousandSeparator={true}
            isAllowed={(values) => {
              const totalCharacters = values.value
                .split("")
                .filter((char) => char !== " ").length;
              return totalCharacters <= 16;
            }}
          />
        </div>
        {!isValid && lastUpdate === "min" && (
          <div className="mx-4 mt-1 text-sm text-red-400">
            <span className="break-words">
              Minimum amount must be equal to or less than maximum amount
            </span>
          </div>
        )}
      </div>
      <div className="maximum-container flex w-full flex-wrap bg-transparent px-2">
        <FormLabel className="pl-1.5 text-xs text-neutral-500">
          Maximum
        </FormLabel>
        <div className={"w-full " + styles.currencyWrapper}>
          <NumericFormat
            allowedDecimalSeparators={[","]}
            allowNegative={false}
            placeholder="0.00"
            id="maximum-amount-filter"
            className={`h-10 w-full rounded-1.5lg border pl-38 pr-4 text-sm leading-normal text-neutral-900 outline-none focus:border-neutral-900 ${
              !isValid && lastUpdate === "max" && "border-red-400"
            }`}
            value={maxValue || ""}
            onValueChange={(value) => {
              setMaxValue(value.floatValue);
              setLastUpdated("max");
            }}
            decimalScale={2}
            fixedDecimalScale
            thousandSeparator={true}
            isAllowed={(values) => {
              const totalCharacters = values.value
                .split("")
                .filter((char) => char !== " ").length;
              return totalCharacters <= 16;
            }}
          />
        </div>
        {!isValid && lastUpdate === "max" && (
          <div className="mx-4 mt-1 text-sm text-red-400">
            <span className="break-words">
              Maximum amount must be equal to or greater than the minimum amount
            </span>
          </div>
        )}
      </div>
      <div className="mt-8 flex w-full flex-row flex-wrap justify-between px-2 visited:bg-transparent hover:bg-transparent">
        <div className="my-auto w-1/2">
          <ButtonComponent
            label="Clear all"
            disabled={!minValue && !maxValue}
            className="inline-flex h-9 w-102 items-center justify-center gap-2 rounded-lg px-2 py-2 text-center text-sm text-zinc-600 hover:text-zinc-900 hover:underline disabled:text-gray-300 disabled:no-underline"
            onClick={() => {
              setMinValue(undefined);
              setMaxValue(undefined);
              clearFilter();
            }}
            size="small"
            type="tertiary"
            id="clear-all-amount-button"
          />
        </div>
        <div className="w-1/2">
          <ButtonComponent
            label="Apply"
            disabled={(!minValue && !maxValue) || !isValid}
            className="inline-flex h-9 w-102 items-center justify-center gap-2 rounded-lg bg-red-600 px-2 py-2 text-center text-sm text-white disabled:bg-zinc-100 disabled:text-gray-300"
            onClick={() => {
              props.apply(minValue as number, maxValue as number);
            }}
            size="medium"
            type="primary"
            id=""
          />
        </div>
      </div>
    </div>
  );
});

export default AmountTransactionFilter;
